import { Component } from '@theme/component';
import { oncePerEditorSession } from '@theme/utilities';

/**
 * @typedef {object} ComparisonSliderRefs
 * @property {HTMLElement} mediaWrapper
 * @property {HTMLInputElement} slider
 * @property {HTMLElement} afterImage
 */

/** @extends {Component<ComparisonSliderRefs>} */
export class ComparisonSliderComponent extends Component {
  requiredRefs = ['mediaWrapper', 'slider', 'afterImage'];

  constructor() {
    super();
    this.hasAnimated = false;
    this.boundHandleIntersection = this.handleIntersection.bind(this);
    this.pointerStart = null;
  }

  connectedCallback() {
    super.connectedCallback();

    const { mediaWrapper, slider } = this.refs;
    this.orientation = mediaWrapper.dataset.orientation || 'horizontal';

    this.sync();
    this.setupIntersectionObserver();

    slider.addEventListener('pointerdown', this.handlePointerDown);
    slider.addEventListener('pointerup', this.handlePointerUp);
    slider.addEventListener('pointercancel', this.handlePointerCancel);
  }

  handlePointerDown = (event) => {
    const { mediaWrapper } = this.refs;
    const rect = mediaWrapper.getBoundingClientRect();
    const compare = Number(
      getComputedStyle(mediaWrapper).getPropertyValue('--compare')
    ) || 50;

    this.pointerStart = {
      x: event.clientX,
      y: event.clientY,
      rect,
      compare
    };
  };

  handlePointerUp = (event) => {
    const start = this.pointerStart;
    this.pointerStart = null;
    if (!start) return;

    const movement = Math.hypot(
      event.clientX - start.x,
      event.clientY - start.y
    );

    // ドラッグした場合はリンクを開かず、比較操作を優先
    if (movement > 8) return;

    const { rect, compare } = start;
    let side;
    let distanceFromHandle;

    if (this.orientation === 'vertical') {
      const boundary = rect.top + rect.height * (1 - compare / 100);
      distanceFromHandle = Math.abs(start.y - boundary);
      side = start.y < boundary ? 'before' : 'after';
    } else {
      const boundary = rect.left + rect.width * (compare / 100);
      distanceFromHandle = Math.abs(start.x - boundary);
      side = start.x < boundary ? 'before' : 'after';
    }

    // 中央のハンドル付近はリンクを開かず、比較操作に使う
    if (distanceFromHandle < 32) return;

    const link =
      side === 'before'
        ? this.dataset.beforeLink
        : this.dataset.afterLink;

    if (link) {
      window.location.assign(link);
    }
  };

  handlePointerCancel = () => {
    this.pointerStart = null;
  };

  sync() {
    const { mediaWrapper, slider } = this.refs;
    if (this.isAnimating) return;

    const val =
      (Number(slider.value) - Number(slider.min)) /
      (Number(slider.max) - Number(slider.min));

    const compareValue = Math.round(val * 100);
    mediaWrapper.style.setProperty('--compare', String(compareValue));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { slider } = this.refs;
    slider?.removeEventListener('pointerdown', this.handlePointerDown);
    slider?.removeEventListener('pointerup', this.handlePointerUp);
    slider?.removeEventListener('pointercancel', this.handlePointerCancel);

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  setValue(value) {
    const { slider } = this.refs;
    if (!slider) return;

    slider.value = String(value);
    this.sync();
  }

  animateSlider() {
    const { mediaWrapper, slider } = this.refs;
    if (this.hasAnimated) return;

    this.hasAnimated = true;
    this.isAnimating = true;
    mediaWrapper.style.setProperty('--transition-duration', '0.5s');

    setTimeout(() => {
      mediaWrapper.style.setProperty('--compare', '40');
    }, 100);

    setTimeout(() => {
      mediaWrapper.style.setProperty('--compare', '60');
    }, 600);

    setTimeout(() => {
      mediaWrapper.style.setProperty('--compare', '50');
    }, 1100);

    setTimeout(() => {
      mediaWrapper.style.setProperty('--transition-duration', '0s');
      slider.value = '50';
      this.isAnimating = false;
    }, 1600);
  }

  setupIntersectionObserver() {
    if (!window.IntersectionObserver) return;

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };

    this.intersectionObserver = new IntersectionObserver(
      this.boundHandleIntersection,
      options
    );
    this.intersectionObserver.observe(this);
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !this.hasAnimated) {
        setTimeout(() => {
          oncePerEditorSession(
            this,
            'comparison-slider-animated',
            () => this.animateSlider()
          );
        }, 300);

        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }
      }
    });
  }
}

if (!customElements.get('comparison-slider-component')) {
  customElements.define(
    'comparison-slider-component',
    ComparisonSliderComponent
  );
}
